import { useEffect, useState } from 'react';
import { Button } from '../../tailwindcomponents';
import { DatePicker } from '../../MaterialUiComponents';
import { Autocomplete, TextField, Typography, LinearProgress, Input, Tabs, SelectMultiple } from '../../tailwindcomponents';
import Overview from './Overview';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import Warranty from './Warranty';
import EditCageCodes from './EditCageCodes';
import { dictionary, warrantyRequest } from '../../types/warranty';
import WarrantyApi from '../../api/warranty';
import EditHourlyRate from './EditHourlyRate';
import taskApi from './../../api/tasks';
import { useLocation } from 'react-router-dom';
import { SelectObject } from '../../types/certificates';
import { createOptionsFromArray, toggleSelectAll } from '../../utils/selectMultiple';
import { claimClassificationOptions, claimStatusOptions } from '../../properties/warrantiesProperties';
import { useQueryTab } from '../../utils/usePersistedTab';
 
// Main Warranty page. Includes the search bar on top, tab control and displays sub-components based on tabValue
function Warranties() {
    const store = useSelector((state: RootState) => state);
    const isWarrantyAdmin = store.user.me.user_clearence_groups.includes("Warranty Edit");
    const { tabValue, setTabValue } = useQueryTab("tab", 0);
    const [loading, setLoading] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [faultBarcode, setFaultBarcode] = useState<string>("");
    const [urlBarcode, setUrlBarcode] = useState<string>("");
    const [cageCodes, setCageCodes] = useState<dictionary[]>([]);
 
    const [warrantiesResults, setWarrantiesResults] = useState<warrantyRequest[]>([]);
    const [allWarranties, setAllWarranties] = useState<warrantyRequest[]>([]); 
    const [selectedWarrantyId, setSelectedWarrantyId] = useState<number | null>(null);
 
    const [selectedstatus, setSelectedstatus] = useState<string[]>([]);
 
    const [selectedClassification, setSelectedClassification] = useState<string[]>([]);
 
    //For Tails select and toggle
    const [tailOptions, setTailOptions] = useState<SelectObject[]>([]);
    const [selectedTails, setSelectedTails] = useState<number[]>([]);
    const [toggleSelectTails, setToggleSelectTails] = useState(false);
 
    const [selectedCageCode, setSelectedCageCode] = useState<dictionary | null>(null);
 
    const [partNumber, setPartNumber] = useState<string>("");
    const location = useLocation();
    let innitalRender = 0;

    // Fetches cage codes on render if cageCodes array is empty
    useEffect(() => {
        refresh();
    }, [startDate, endDate, selectedstatus, faultBarcode, selectedTails, partNumber, selectedClassification, selectedCageCode]);
 
    useEffect(() => {
        taskApi.GetAllTailNumbers().then(res => {
            const newTailOptions = createOptionsFromArray(res.data);
            setTailOptions(newTailOptions);
        });
        getBarcodeFromUrl();
    }, [])
 
    const refresh = () => {
        setLoading(true);
        WarrantyApi.SearchForAllWarrantyRequests(
            startDate,
            endDate,
            selectedstatus,
            faultBarcode,
            selectedTails.map(index => tailOptions[index]?.name).filter(Boolean),
            partNumber,
            selectedClassification,
            selectedCageCode?.name !== undefined ? [selectedCageCode?.name] : []
        ).then(res => {
            if (res.status === true) {
                setWarrantiesResults(res.data);
                setLoading(false);
                if(innitalRender === 0){
                    innitalRender = 1;
                    setAllWarranties(res.data); // only setting allWarranties on first render
                }   
            }
        });
        WarrantyApi.getAllCageCodes().then(res => {
            setCageCodes(res.data.filter(cageCode => cageCode.visible === true));
        });
    }
    const changeTab = (tabValue: number) => {
        if (tabValue !== 3) {
            setSelectedWarrantyId(null);
        }
        if (tabValue === 0) {
            refresh();
        }
        setTabValue(tabValue)
    }
    const onResetFields = () => {
        setStartDate(null);
        setEndDate(new Date());
        setSelectedstatus([]);
        setFaultBarcode('');
        setSelectedTails([]);
        setSelectedClassification([]);
        setSelectedCageCode(null);
    }
    const openWarranty = (warrantyId: number) => {
        setSelectedWarrantyId(warrantyId);;
        setTabValue(3);
    }
    const returnToOverview = () => {
        refresh();
        changeTab(0);
    }
    const getBarcodeFromUrl = () => {
        let barcode;
        let loc;
        let url = location.search;
        if (url.includes("=")) {
            loc = url.split("=");
            barcode = loc[loc.length - 1];
            setUrlBarcode(barcode);
            setTabValue(3);
        }
    }
 
    const tabNames = isWarrantyAdmin ? ["Overview", "Warranty Defender","Warranty Stats & Estimates", "Create new Warranty Request", "Edit Cage Codes", "Edit Hourly Rate"] : ["Overview"];
    return (
        <div className="w-full">
            {loading === true && <LinearProgress />}
            <Tabs
                tabNames={tabNames}
                onChangeTabValue={(tabValue: number) => changeTab(tabValue)}
                tabValue={tabValue}
            />
            {tabValue === 0 && warrantiesResults !== undefined &&
                <div className="w-full">
                    <div className="ds-filter-bar">
                        <Input
                            multiline={false}
                            label_text="Fault barcode"
                            help_text="Please type/paste in barcode for a fault"
                            type="text"
                            value={faultBarcode}
                            width={90}
                            onTextChange={(value: string) => setFaultBarcode(value)}
                        />
                        <Input multiline={false} help_text={""} label_text={"Part number"} value={partNumber} onTextChange={(text: string) => { setPartNumber(text) }} type="text" width={80} />
                        <SelectMultiple customSelectedText=" tail's select" optionsObject={tailOptions} selectedItems={selectedTails} width={200} onSelectedItem={(e: any) => { setSelectedTails(e.target.value); toggleSelectAll(e.target.value, tailOptions, setSelectedTails, toggleSelectTails, setToggleSelectTails); }} filter_by_text="Tails" />
                        <Autocomplete
                            options={cageCodes}
                            getOptionLabel={option => option.name}
                            value={selectedCageCode}
                            onChange={(
                                _event: any,
                                newValue: dictionary | string | null
                            ) => {
                                if (newValue && typeof newValue !== 'string') setSelectedCageCode(newValue);
                                else setSelectedCageCode(null);
                            }}
                            renderInput={params => (
                                <TextField {...params} label="Cage Code" variant="standard" />
                            )}
                        />
                        <DatePicker type="Date" selected={startDate} label="Created date from" onDateChange={(date: Date) => setStartDate(date)} />
                        <DatePicker type="Date" selected={endDate} label="Created date to" onDateChange={(date: Date) => setEndDate(date)} />
                        <SelectMultiple
                            customSelectedText=" status's select"
                            options={Object.values(claimStatusOptions)}
                            selectedItemsString={selectedstatus}
                            width={200}
                            onSelectedItem={(e: any) => { setSelectedstatus(e.target.value); }}
                            filter_by_text="Status"
                        />
                        <SelectMultiple
                            customSelectedText=" classes selected"
                            options={Object.values(claimClassificationOptions)}
                            selectedItemsString={selectedClassification}
                            width={200}
                            onSelectedItem={(e: any) => { setSelectedClassification(e.target.value); }}
                            filter_by_text="Classifications"
                        />
                        <div className="ds-filter-actions">
                            <Button variant="text" color="primary" size="small" onClick={() => onResetFields()}>Reset filters</Button>
                        </div>
                    </div>
                    <div className="ds-filter-summary">
                        <Typography text={warrantiesResults !== undefined ? warrantiesResults.length + " reports found" : '0 reports found'} />
                    </div>
                    <Overview selectWarranty={(warrantyId: number) => { openWarranty(warrantyId) }} allWarranties={warrantiesResults} setWarrantiesResults={setWarrantiesResults} isWarrantyAdmin={isWarrantyAdmin} />
                </div>
            }
            {tabValue === 1 && <div>
                <iframe id="warranty-defender" title="Warranty Defender" width="100%" height="1200px" src={process.env.REACT_APP_WARRANTY_DEFENDER_BI} allowFullScreen={true}></iframe>
            </div>}
            {tabValue === 2 && <div>
                <iframe id="warranty-stats-and-estimates" title="Warranty Stats & Estimates" width="100%" height="1200px" src={process.env.REACT_APP_WARRANTY_STATS_AND_ESTIMATES_BI} allowFullScreen={true}></iframe>
            </div>}
            {(tabValue === 3) &&
                <div className="w-full">
                    <Warranty allWarranties={allWarranties} refreshOverview={refresh} setTabValue={setTabValue} urlBarcode={urlBarcode} loading={loading} setLoading={(loadStatus: boolean) => setLoading(loadStatus)} returnToOverview={() => returnToOverview()} cageCodes={cageCodes} warrantyId={selectedWarrantyId} />
                </div>
            }
            {tabValue === 4 &&
                <div className="w-full">
                    <EditCageCodes refreshCageCodes={() => refresh()} />
                </div>
            }
            {tabValue === 5 &&
                <div className="w-full">
                    <EditHourlyRate refreshHourlyRates={() => refresh()} />
                </div>
            }
        </div>
    );
}
export default Warranties;